import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Checkbox, Form } from 'antd';
import { Rule } from 'antd/es/form';
import { IEditFormComponentInfo, MfaType } from '../../../interfaces';
import {
    useCatalogCompanyTypeFilterNotMNOSelect,
    useCatalogCompanyTypeSelect,
    useCheckCustomerPrivacy,
    useCompaniesSelect,
    useCompanyConfig,
    useCompanyInfo,
    useCompanyIsMNOHost,
    useIsAbleToManageCompanyGeolocation,
    useMFASelect,
    useMyCompanySelect,
    useProfile,
    useProfileIsRight,
    useUsersSelect,
} from '../../../hooks';
import { apiMessagesGetFormRules, setCompanyConfigMFA } from '../../../middleware';
import { formatDataTestAttribute } from '../../../instruments';
import { tF, tP, tV } from '../../../translate';
import {
    CardInfoToolBar,
    CardMainLayout,
    Catcher,
    FormSelect,
    InputDebounce,
    TextAreaDebounce,
    cardLayoutOptions,
} from '../../../Components';
import { EditCompanyInfoTenantConfig } from '../Components';

export const EditCompanyInfo: React.FC<IEditFormComponentInfo> = observer(
    ({ isMyCompany, isNew, isSending, save }: IEditFormComponentInfo) => {
        const [currentName, setName] = useState('');
        const { whiteLabel, companyId: myCompanyId } = useProfile();
        const { id: currentCompanyId, name, isHashed } = useCompanyInfo();
        const isLord = useProfileIsRight('LORD');
        const isAdmin = useProfileIsRight('ADMIN');
        const isCompanyMNOHost = useCompanyIsMNOHost();
        const { isMainMaternalCompany } = useProfile();
        const companyTypeSelectHook = useCatalogCompanyTypeSelect;
        const parentsSelectHook = useCompaniesSelect;
        const myCompanySelectHook = useMyCompanySelect;
        const usersSelectHook = useUsersSelect;
        const isCheckCustomerPrivacy = useCheckCustomerPrivacy();
        const { mfaForcedBy } = useCompanyConfig();

        const validateAPIAndName: Array<Rule> = [
            { required: true, message: tV('Please input Name.') },
            ...apiMessagesGetFormRules,
        ];
        const validateAPIAndCompanyType: Array<Rule> = [
            { required: true, message: tV('Please select Company Type.') },
            ...apiMessagesGetFormRules,
        ];
        const validateAPIAndParent: Array<Rule> = [
            { required: true, message: tV('Please select Parent.') },
            ...apiMessagesGetFormRules,
        ];

        const isGeolocationManageable = useIsAbleToManageCompanyGeolocation(isMyCompany);
        const selectHook = isMyCompany ? myCompanySelectHook : parentsSelectHook;
        const onChangeMFA = (mfa: string) => {
            setCompanyConfigMFA(mfa as MfaType);
        };
        useEffect(() => {
            if (name !== undefined && currentName === '' && !isNew) {
                setName(name);
            }
        }, [name]);

        const nameTitle = tF('Name');
        const notesTitle = tF('Notes');
        const stepAuthTitle = `2-Step Authentication Enforcement ${
            mfaForcedBy ? '(' + tF('Enforced by') + ' ' + mfaForcedBy + ')' : ''
        }`;

        return (
            <Catcher>
                <CardMainLayout
                    columnOption={cardLayoutOptions.cardEditCompany}
                    idSpinners={[
                        'DELETE_COMPANY_TENANT_CONFIG',
                        'GET_COMPANIES_CHILDREN_SELECT',
                        'GET_COMPANIES_SELECT',
                        'GET_COMPANY',
                        'GET_COMPANY_TYPES_SELECT',
                        'GET_PAYMENT_SELECT',
                        'PATCH_COMPANY',
                        'PATCH_COMPANY_CONFIG',
                        'POST_COMPANY',
                        'PUT_COMPANY_TENANT_CONFIG',
                    ]}
                    isEdit
                    name={myCompanyId === currentCompanyId ? tP('My Company') : currentName}
                    dataTest={'EditCompanyInfo'}
                >
                    <Form.Item
                        name="name"
                        label={nameTitle}
                        rules={validateAPIAndName}
                        data-test={formatDataTestAttribute('EditCompanyInfo', 'Name', 'FormItem')}
                    >
                        <InputDebounce
                            disabled={isHashed}
                            placeholder={nameTitle}
                            isFirstInput
                            data-test={formatDataTestAttribute('EditCompanyInfo', 'Name', 'Input')}
                        />
                    </Form.Item>
                    <FormSelect
                        fieldName={'companyTypeId'}
                        label={tF('Company Type')}
                        rules={validateAPIAndCompanyType}
                        useSelectHook={
                            isCompanyMNOHost || (isNew && isMainMaternalCompany)
                                ? companyTypeSelectHook
                                : useCatalogCompanyTypeFilterNotMNOSelect
                        }
                        disabled={isMyCompany || (!isNew && isCompanyMNOHost)}
                        dataTest={'EditCompanyInfoCompanyType'}
                    />
                    <FormSelect
                        fieldName={'parentId'}
                        label={tF('Parent')}
                        rules={validateAPIAndParent}
                        useSelectHook={selectHook}
                        // disabled={isMyCompany}
                        disabled={!isNew}
                        dataTest={'EditCompanyInfoParent'}
                    />
                    <FormSelect
                        fieldName={'primaryContactId'}
                        label={tF('Primary contact')}
                        rules={apiMessagesGetFormRules}
                        useSelectHook={usersSelectHook}
                        dataTest={'EditCompanyInfoPrimaryContact'}
                    />
                    <FormSelect
                        fieldName={'technicalContactId'}
                        label={tF('Technical contact')}
                        rules={apiMessagesGetFormRules}
                        useSelectHook={usersSelectHook}
                        dataTest={'EditCompanyInfoTechnicalContact'}
                    />
                    <FormSelect
                        fieldName={'financialContactId'}
                        label={tF('Financial contact')}
                        rules={apiMessagesGetFormRules}
                        useSelectHook={usersSelectHook}
                        dataTest={'EditCompanyInfoParentFinancialContact'}
                    />

                    <Form.Item
                        name={'externalId'}
                        label={tF('External ID')}
                        rules={apiMessagesGetFormRules}
                        data-test={'EditCompanyInfoExternalId'}
                    >
                        <InputDebounce placeholder={tF('External ID')} data-test={'EditCompanyInfoExternalIdInput'} />
                    </Form.Item>
                    <Form.Item
                        name={'notes'}
                        label={notesTitle}
                        rules={apiMessagesGetFormRules}
                        data-test={'EditCompanyInfoNote'}
                    >
                        <TextAreaDebounce
                            placeholder={notesTitle}
                            autoSize={true}
                            data-test={'EditCompanyInfoNoteTextArea'}
                        />
                    </Form.Item>

                    <Form.Item
                        name={'privacyCdr'}
                        valuePropName="checked"
                        rules={apiMessagesGetFormRules}
                        data-test={'EditCompanyInfoPrivacyCdr'}
                    >
                        <Checkbox data-test={'EditCompanyInfoPrivacyCdrCheckBox'}>{tF('CDR Privacy')}</Checkbox>
                    </Form.Item>
                    {(!isMyCompany && whiteLabel) || (!isMyCompany && isLord) ? (
                        <Form.Item
                            name={'whiteLabel'}
                            valuePropName="checked"
                            rules={apiMessagesGetFormRules}
                            data-test={'EditCompanyInfoWhiteLabel'}
                        >
                            <Checkbox data-test={'EditCompanyInfoWhiteCheckBox'}>
                                {tF('White labeling for company')}
                            </Checkbox>
                        </Form.Item>
                    ) : null}
                    {isCheckCustomerPrivacy && !isNew ? (
                        <Form.Item
                            name={'customerPrivacy'}
                            valuePropName="checked"
                            rules={apiMessagesGetFormRules}
                            data-test={'EditCompanyInfoCustomerPrivacy'}
                        >
                            <Checkbox data-test={'EditCompanyInfoCustomerPrivacyCheckbox'}>
                                {tF('Customer Privacy')}
                            </Checkbox>
                        </Form.Item>
                    ) : null}
                    {isGeolocationManageable ? (
                        <>
                            <Form.Item
                                name={'geolocationEnabled'}
                                valuePropName="checked"
                                rules={apiMessagesGetFormRules}
                                data-test={'EditCompanyInfoGeolocationEnabled'}
                            >
                                <Checkbox data-test={'EditCompanyInfoGeolocationEnabledCheckBox'}>
                                    {tF('Activate Geolocation for all SIM cards in the account')}
                                </Checkbox>
                            </Form.Item>
                            <Form.Item
                                name={'geotrackingEnabled'}
                                valuePropName="checked"
                                rules={apiMessagesGetFormRules}
                                data-test={'EditCompanyInfoGeotrackingEnabled'}
                            >
                                <Checkbox data-test={'EditCompanyInfoGeotrackingEnabledCheckBox'}>
                                    {tF('Activate Geotracking for all SIM cards in the account')}
                                </Checkbox>
                            </Form.Item>
                        </>
                    ) : null}

                    <FormSelect
                        fieldName={'mfa'}
                        label={stepAuthTitle}
                        useSelectHook={useMFASelect}
                        dataTest={'mfaSelector'}
                        onChange={(mfa) => onChangeMFA(mfa as string)}
                        disabled={!!mfaForcedBy && !isAdmin}
                    />
                    <EditCompanyInfoTenantConfig />

                    <CardInfoToolBar dataTest={'EditCompanyInfo'} isLoading={isSending} isNew={isNew} onSave={save} />
                </CardMainLayout>
            </Catcher>
        );
    },
);
